import { useState } from "react";
import toast from "react-hot-toast";
import { Landmark, Plus, AlertTriangle } from "lucide-react";
import { useTaxObligations, useUpdateTaxObligation, TAX_STATUS_LABEL, TaxObligationStatus, isTaxOverdue } from "@/api/taxes";
import { getApiErrorMessage } from "@/api/client";
import { useAuthStore } from "@/stores/authStore";
import CreateTaxObligationModal from "@/pages/taxes/CreateTaxObligationModal";

const STATUS_STYLE: Record<TaxObligationStatus, string> = {
  PENDIENTE: "bg-gray-100 text-gray-600",
  EN_PREPARACION: "bg-blue-100 text-blue-700",
  EN_REVISION: "bg-purple-100 text-purple-700",
  PRESENTADO: "bg-teal-100 text-teal-700",
  PAGADO: "bg-green-100 text-green-700",
  VENCIDO: "bg-red-100 text-red-700",
};

const STATUS_OPTIONS = Object.keys(TAX_STATUS_LABEL) as TaxObligationStatus[];

export default function TaxesSection({ clientId }: { clientId: string }) {
  const hasPermission = useAuthStore((s) => s.hasPermission);
  const { data, isLoading } = useTaxObligations({ clientId, pageSize: 50 });
  const updateTaxObligation = useUpdateTaxObligation();
  const [showCreate, setShowCreate] = useState(false);

  const handleStatusChange = async (id: string, status: TaxObligationStatus) => {
    try {
      await updateTaxObligation.mutateAsync({ id, status });
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  return (
    <div className="card p-6">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-900">Impuestos</h2>
        {hasPermission("taxes.create") && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-xs font-medium text-brand-600 hover:text-brand-800"
            onClick={() => setShowCreate(true)}
          >
            <Plus size={14} /> Nueva obligación
          </button>
        )}
      </div>

      <div className="mt-4 space-y-2">
        {isLoading && <p className="text-sm text-gray-400">Cargando obligaciones tributarias...</p>}
        {!isLoading && (!data?.data || data.data.length === 0) && (
          <p className="text-sm text-gray-400">Este cliente aún no tiene obligaciones tributarias registradas.</p>
        )}
        {data?.data.map((taxObligation) => (
          <div key={taxObligation.id} className="flex items-center justify-between rounded-md border border-gray-100 p-3">
            <div className="flex items-center gap-3 overflow-hidden">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600">
                <Landmark size={15} />
              </div>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {taxObligation.taxType} · {taxObligation.periodLabel}
                </p>
                <p className="text-xs text-gray-500">
                  {taxObligation.dueDate && (
                    <span className={isTaxOverdue(taxObligation) ? "inline-flex items-center gap-1 text-red-600" : ""}>
                      {isTaxOverdue(taxObligation) && <AlertTriangle size={10} className="inline" />} Vence {taxObligation.dueDate}
                    </span>
                  )}
                </p>
              </div>
            </div>
            {hasPermission("taxes.update") ? (
              <select
                className={`shrink-0 rounded-full border-0 px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[taxObligation.status]}`}
                value={taxObligation.status}
                onChange={(e) => handleStatusChange(taxObligation.id, e.target.value as TaxObligationStatus)}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {TAX_STATUS_LABEL[s]}
                  </option>
                ))}
              </select>
            ) : (
              <span className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[taxObligation.status]}`}>
                {TAX_STATUS_LABEL[taxObligation.status]}
              </span>
            )}
          </div>
        ))}
      </div>

      {showCreate && <CreateTaxObligationModal clientId={clientId} onClose={() => setShowCreate(false)} />}
    </div>
  );
}
